// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Admin Dashboard
// Operator controls, revenue tracking, compliance reports
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasino } from '../context/CasinoShell';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { transactionHistory, fairnessLog, sessionStats } = useCasino();
  const [activeTab, setActiveTab] = useState('overview');

  // Calculate admin metrics from transaction history
  const totalDeposits = transactionHistory.filter(t => t.type === 'deposit').reduce((s, t) => s + t.amount, 0);
  const totalWithdrawals = transactionHistory.filter(t => t.type === 'withdraw').reduce((s, t) => s + t.amount, 0);
  const totalBets = transactionHistory.filter(t => t.type === 'loss').reduce((s, t) => s + t.amount, 0);
  const totalPayouts = transactionHistory.filter(t => t.type === 'win').reduce((s, t) => s + t.amount, 0);
  const houseRevenue = totalBets - totalPayouts;
  const totalSpins = fairnessLog.length;
  const rtp = totalBets > 0 ? ((totalPayouts / totalBets) * 100).toFixed(2) : '0.00';

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📊 ADMIN DASHBOARD</h1>
      <p style={styles.subtitle}>Operator controls & compliance monitoring</p>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['overview', 'revenue', 'compliance', 'players'].map(tab => (
          <button key={tab} style={{ ...styles.tab, background: activeTab === tab ? '#DC2626' : '#1a1a1a', color: activeTab === tab ? '#fff' : '#888' }}
            onClick={() => setActiveTab(tab)}>{tab.toUpperCase()}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* KPI Cards */}
          <div style={styles.kpiGrid}>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>TOTAL DEPOSITS</span>
              <span style={{ ...styles.kpiValue, color: '#00FF88' }}>${totalDeposits.toFixed(2)}</span>
            </div>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>TOTAL WITHDRAWALS</span>
              <span style={{ ...styles.kpiValue, color: '#FFD700' }}>${totalWithdrawals.toFixed(2)}</span>
            </div>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>TOTAL WAGERED</span>
              <span style={styles.kpiValue}>${totalBets.toFixed(2)}</span>
            </div>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>TOTAL PAYOUTS</span>
              <span style={styles.kpiValue}>${totalPayouts.toFixed(2)}</span>
            </div>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>HOUSE REVENUE</span>
              <span style={{ ...styles.kpiValue, color: houseRevenue >= 0 ? '#00FF88' : '#DC2626' }}>
                ${houseRevenue.toFixed(2)}
              </span>
            </div>
            <div style={styles.kpiCard}>
              <span style={styles.kpiLabel}>ACTUAL RTP</span>
              <span style={{ ...styles.kpiValue, color: parseFloat(rtp) >= 85 ? '#00FF88' : '#DC2626' }}>
                {rtp}%
              </span>
            </div>
          </div>

          {/* Spin Activity */}
          <div style={styles.activityCard}>
            <h3 style={styles.sectionTitle}>ACTIVITY</h3>
            <div style={styles.activityGrid}>
              <div style={styles.activityStat}>
                <span style={styles.activityLabel}>Total Spins</span>
                <span style={styles.activityValue}>{totalSpins}</span>
              </div>
              <div style={styles.activityStat}>
                <span style={styles.activityLabel}>Avg Bet</span>
                <span style={styles.activityValue}>
                  ${totalSpins > 0 ? (totalBets / totalSpins).toFixed(2) : '0.00'}
                </span>
              </div>
              <div style={styles.activityStat}>
                <span style={styles.activityLabel}>House Edge</span>
                <span style={styles.activityValue}>{(100 - parseFloat(rtp)).toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'compliance' && (
        <div style={styles.complianceSection}>
          <div style={styles.complianceCard}>
            <h3 style={styles.sectionTitle}>REGULATORY COMPLIANCE</h3>
            <div style={styles.complianceChecks}>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>{parseFloat(rtp) >= 85 ? '✅' : '❌'}</span>
                <span style={styles.checkText}>RTP meets AGLC minimum (85%): {rtp}%</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Provably fair SHA-256 verification active</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Responsible gaming controls available</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Age verification gate (18+) enforced</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Transaction audit trail maintained</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Self-exclusion system operational</span>
              </div>
              <div style={styles.checkRow}>
                <span style={styles.checkIcon}>✅</span>
                <span style={styles.checkText}>Reality check intervals configured</span>
              </div>
            </div>
          </div>

          <button style={styles.reportBtn} onClick={() => navigate('/admin/compliance-report')}>
            📄 GENERATE COMPLIANCE REPORT
          </button>
        </div>
      )}

      {activeTab === 'revenue' && (
        <div style={styles.revenueSection}>
          <div style={styles.revenueCard}>
            <h3 style={styles.sectionTitle}>REVENUE BREAKDOWN</h3>
            <div style={styles.revenueRow}><span>Deposits In</span><span style={{ color: '#00FF88' }}>+${totalDeposits.toFixed(2)}</span></div>
            <div style={styles.revenueRow}><span>Withdrawals Out</span><span style={{ color: '#DC2626' }}>-${totalWithdrawals.toFixed(2)}</span></div>
            <div style={styles.revenueDivider} />
            <div style={styles.revenueRow}><span>Total Wagered</span><span>${totalBets.toFixed(2)}</span></div>
            <div style={styles.revenueRow}><span>Total Payouts</span><span>-${totalPayouts.toFixed(2)}</span></div>
            <div style={styles.revenueDivider} />
            <div style={{ ...styles.revenueRow, fontWeight: 900 }}>
              <span>NET HOUSE REVENUE</span>
              <span style={{ color: houseRevenue >= 0 ? '#00FF88' : '#DC2626' }}>${houseRevenue.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div style={styles.playersSection}>
          <div style={styles.playerCard}>
            <h3 style={styles.sectionTitle}>PLAYER OVERVIEW</h3>
            <p style={styles.playerNote}>Player management requires database integration. Currently tracking local session data.</p>
            <div style={styles.playerStats}>
              <div style={styles.playerStat}>
                <span style={styles.playerLabel}>Active Sessions</span>
                <span style={styles.playerValue}>1</span>
              </div>
              <div style={styles.playerStat}>
                <span style={styles.playerLabel}>Total Transactions</span>
                <span style={styles.playerValue}>{transactionHistory.length}</span>
              </div>
              <div style={styles.playerStat}>
                <span style={styles.playerLabel}>Fairness Proofs</span>
                <span style={styles.playerValue}>{fairnessLog.length}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 700, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  subtitle: { fontSize: 11, color: '#666', marginTop: 4, marginBottom: 16 },
  tabs: { display: 'flex', gap: 4, marginBottom: 16 },
  tab: { padding: '6px 12px', borderRadius: 6, border: '1px solid #333', fontSize: 10, fontWeight: 700, letterSpacing: 1, cursor: 'pointer' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 },
  kpiCard: { padding: 14, background: '#111', borderRadius: 8, border: '1px solid #222', textAlign: 'center' },
  kpiLabel: { display: 'block', fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 6 },
  kpiValue: { fontSize: 18, fontWeight: 900, color: '#fff', fontFamily: "'Courier New', monospace" },
  activityCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222' },
  sectionTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 12, marginTop: 0 },
  activityGrid: { display: 'flex', gap: 12, justifyContent: 'space-around' },
  activityStat: { textAlign: 'center' },
  activityLabel: { display: 'block', fontSize: 9, color: '#666', marginBottom: 4 },
  activityValue: { fontSize: 16, fontWeight: 900, color: '#fff' },
  complianceSection: {},
  complianceCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 12 },
  complianceChecks: { display: 'flex', flexDirection: 'column', gap: 8 },
  checkRow: { display: 'flex', gap: 10, alignItems: 'center' },
  checkIcon: { fontSize: 14 },
  checkText: { fontSize: 11, color: '#ccc' },
  reportBtn: {
    width: '100%', padding: 12, borderRadius: 8, border: '2px solid #DC2626',
    background: '#DC262233', color: '#DC2626', fontSize: 12, fontWeight: 900,
    letterSpacing: 1, cursor: 'pointer',
  },
  revenueSection: {},
  revenueCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222' },
  revenueRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 12, color: '#ccc' },
  revenueDivider: { borderBottom: '1px solid #222', margin: '4px 0' },
  playersSection: {},
  playerCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222' },
  playerNote: { fontSize: 11, color: '#666', marginBottom: 12 },
  playerStats: { display: 'flex', gap: 12 },
  playerStat: { flex: 1, textAlign: 'center', padding: 10, background: '#0a0a0a', borderRadius: 6 },
  playerLabel: { display: 'block', fontSize: 8, color: '#555', marginBottom: 4, fontWeight: 700 },
  playerValue: { fontSize: 18, fontWeight: 900, color: '#fff' },
};
